import { useRef, useState } from "react";
import { Volume2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { fetchMeaningAudio, resolveAudioSrc } from "@/lib/acharya/acharya-service";

/**
 * Phase 11 — AI voice for meanings, stories, commentary (never Sanskrit chanting).
 */
export function MeaningAudioIcon({
  text,
  language = "en",
  mode = "meaning",
  token,
  title = "Listen",
  size = 16,
  fallbackLanguage = "en",
  fallbackText,
  onError,
  className = "",
}) {
  const [loading, setLoading] = useState(false);
  const [src, setSrc] = useState(null);
  const audioEl = useRef(null);

  const play = async () => {
    const content = String(text ?? "").trim();
    if (!content || loading) return;

    const englishFallback = String(fallbackText ?? text ?? "").trim();

    const tryLang = async (lang, spokenText) => {
      const payload = await fetchMeaningAudio(spokenText, mode, lang, token);
      const s = resolveAudioSrc(payload);
      if (!s) throw new Error("No audio returned");
      return s;
    };

    try {
      setLoading(true);
      if (!src) {
        let resolved = null;
        try {
          resolved = await tryLang(language, content);
        } catch (firstErr) {
          if (language !== fallbackLanguage && englishFallback) {
            resolved = await tryLang(fallbackLanguage, englishFallback);
          } else {
            throw firstErr;
          }
        }
        setSrc(resolved);
        queueMicrotask(() => audioEl.current?.play?.().catch(() => {}));
      } else {
        if (audioEl.current?.paused) {
          await audioEl.current?.play?.().catch(() => {});
        } else {
          audioEl.current.pause();
          audioEl.current.currentTime = 0;
          await audioEl.current?.play?.().catch(() => {});
        }
      }
    } catch (e) {
      onError?.(e);
      toast.error("Could not play audio. Please try again.");
      if (typeof window !== "undefined") {
        console.error("[MeaningAudio]", e);
      }
    } finally {
      setLoading(false);
    }
  };

  if (!text?.trim()) return null;

  return (
    <span className={`inline-flex items-center ${className}`} data-testid="meaning-audio-icon">
      <button
        type="button"
        onClick={play}
        title={title}
        aria-label={loading ? "Loading audio" : title}
        className="inline-flex items-center justify-center rounded-sm text-muted-foreground hover:text-foreground transition-colors"
        style={{ width: size + 6, height: size + 6 }}
      >
        {loading ? (
          <Loader2 width={size} height={size} className="animate-spin" />
        ) : (
          <Volume2 width={size} height={size} />
        )}
      </button>
      <audio ref={audioEl} src={src ?? undefined} preload="auto" />
    </span>
  );
}
